import { supabase } from './supabase';

/**
 * Customer-facing custom domain management for Nexus-hosted websites.
 * Thin client over the vercel-hosting-control edge function's domain actions
 * (the function authorizes the business owner for these).
 */

export interface DomainDnsRecord {
  type: string;
  name: string;
  value: string;
}

export interface CustomDomainStatus {
  name: string;
  verified: boolean;
  misconfigured: boolean;
  dnsRecords: DomainDnsRecord[];
}

export interface HostingDomains {
  /** All domains on the Vercel project. */
  domains: { name: string; verified: boolean }[];
  /** The default *.vercel.app domain — instantly usable preview URL. */
  previewUrl: string | null;
  /** First customer-owned (non *.vercel.app) domain, if any. */
  customDomain: string | null;
}

function invoke(body: Record<string, unknown>) {
  if (!supabase) throw new Error('Supabase client not initialized');
  return supabase.functions.invoke('vercel-hosting-control', { body });
}

async function readError(error: unknown, data: any): Promise<string> {
  if (data?.error && typeof data.error === 'string') return data.error;
  const ctx = (error as any)?.context;
  if (ctx?.text) {
    try {
      const parsed = JSON.parse(await ctx.text());
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON */ }
  }
  return (error as any)?.message || 'Request failed';
}

export async function fetchHostingDomains(businessId: string): Promise<HostingDomains> {
  const { data, error } = await invoke({ businessId, action: 'domains' });
  if (error || !data?.success) throw new Error(await readError(error, data));
  const domains = (data.domains || []) as { name: string; verified: boolean }[];
  const preview = domains.find((d) => d.name.endsWith('.vercel.app')) || null;
  const custom = domains.find((d) => !d.name.endsWith('.vercel.app')) || null;
  return {
    domains,
    previewUrl: preview ? `https://${preview.name}` : null,
    customDomain: custom?.name ?? null,
  };
}

export async function addCustomDomain(businessId: string, domain: string): Promise<CustomDomainStatus> {
  const { data, error } = await invoke({ businessId, action: 'add-domain', domain });
  if (error || !data?.success) throw new Error(await readError(error, data));
  return data.domain as CustomDomainStatus;
}

export async function checkCustomDomain(businessId: string, domain: string): Promise<CustomDomainStatus> {
  const { data, error } = await invoke({ businessId, action: 'domain-status', domain });
  if (error || !data?.success) throw new Error(await readError(error, data));
  return data.domain as CustomDomainStatus;
}
